/**
 * <s-page-fade> — site-wide page transition. Rendered once in the layout.
 * When a shopper follows an internal link, the page content (main + footer)
 * fades to white while the next page loads; the header stays visible.
 *
 * Skips: modified clicks (new tab/window), non-primary buttons, links with a
 * target or download attribute, other origins, same-page #anchors, links inside
 * an element marked [data-no-fade], and prefers-reduced-motion.
 * Clears the fade when a page is restored from the back/forward cache.
 */
class SPageFade extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    document.addEventListener('click', this.#handleClick, { signal: this.#ac.signal });
    window.addEventListener('pageshow', this.#reset, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #handleClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href]');
    if (!link || link.closest('[data-no-fade]')) return;
    if ((link.target && link.target !== '_self') || link.hasAttribute('download')) return;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    event.preventDefault();
    document.documentElement.setAttribute('data-page-fade', 'out');
    setTimeout(() => {
      window.location.href = url.href;
    }, 350);
  };

  #reset = () => {
    document.documentElement.removeAttribute('data-page-fade');
  };
}

if (!customElements.get('s-page-fade')) {
  customElements.define('s-page-fade', SPageFade);
}
